// LoginPage.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from './utills/axiosInstance';

const LoginPage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);

  // Microsoft OAuth Config
  const clientId = process.env.REACT_APP_CLIENT_ID;
  const tenantId = process.env.REACT_APP_TENANT_ID;
  const redirectUri = encodeURIComponent(process.env.REACT_APP_REDIRECT_URI);
  const scope = encodeURIComponent(process.env.REACT_APP_BACKEND_SCOPE);

  const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}&response_mode=query&scope=${scope}`;

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token && !profile) {
      axiosInstance
        .get("/auth/user")
        .then((res) => {
          setProfile(res.data);
          const role = res.data.role;
          if (role === 'admin') {
            navigate('/admindashboard');
          } else {
            navigate('/dashboard');
          }
        })
        .catch(() => {
          localStorage.removeItem("token");
          setError("Session expired. Please log in again.");
        });
    }
  }, [profile, navigate]);

  const handleLogin = () => {
    window.location.href = authUrl;
  };

  const handleLogout = () => {
    setProfile(null);
    localStorage.removeItem('token');
    const postLogoutRedirectUri = encodeURIComponent(process.env.REACT_APP_REDIRECT_URI);
    const logoutUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/logout?post_logout_redirect_uri=${postLogoutRedirectUri}`;
    window.location.href = logoutUrl;
  };

  return (
    <>
      {/* ==================== SIMPLE INTERNAL CSS ==================== */}
      <style>
{`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body, html {
    height: 100%;
    font-family: 'Arial', sans-serif;
  }

  .login-container {
    min-height: 100vh;
    background: #f0f2f5;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }

  .login-box {
    background: white;
    width: 100%;
    max-width: 400px;
    padding: 40px 30px;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    text-align: center;
  }

  .app-title {
    font-size: 28px;
    color: #1a1a1a;
    margin-bottom: 8px;
    font-weight: 600;
  }

  .app-subtitle {
    color: #666;
    font-size: 14px;
    margin-bottom: 30px;
  }

  .error {
    background: #ffebeb;
    color: #d32f2f;
    padding: 10px;
    border-radius: 8px;
    font-size: 14px;
    margin-bottom: 20px;
    border: 1px solid #ffcdd2;
  }

  .profile-info h2 {
    font-size: 20px;
    color: #333;
    margin: 15px 0 5px;
  }

  .profile-info h3 {
    font-size: 18px;
    color: #0078d4;
    margin-bottom: 5px;
  }

  .profile-info p {
    color: #555;
    font-size: 14px;
    margin-bottom: 20px;
  }

  .btn {
    width: 100%;
    padding: 12px;
    font-size: 16px;
    font-weight: 500;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-microsoft {
    background: #0078d4;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }

  .btn-microsoft:hover {
    background: #106ebe;
  }

  .btn-logout {
    background: #f5f5f5;
    color: #d32f2f;
    border: 1px solid #ddd;
  }

  .btn-logout:hover {
    background: #ffebee;
  }

  .ms-logo {
    width: 20px;
    height: 20px;
  }

  .footer {
    margin-top: 30px;
    font-size: 12px;
    color: #999;
  }

  /* Mobile Responsive */
  @media (max-width: 480px) {
    .login-box {
      padding: 30px 20px;
    }
    .app-title {
      font-size: 24px;
    }
  }
`}
      </style>

      {/* ==================== JSX CONTENT ==================== */}
      <div className="login-container">
        <div className="login-box">
          <h1 className="app-title">Dealer Task App</h1>
          <p className="app-subtitle">Sign in to manage your tasks</p>

          {error && <div className="error">{error}</div>}

          {profile ? (
            <div className="profile-info">
              <h2>Welcome back!</h2>
              <h3>{profile.displayName}</h3>
              <p>{profile.mail || profile.userPrincipalName}</p>
              <button onClick={handleLogout} className="btn btn-logout">
                Logout
              </button>
            </div>
          ) : (
            <button onClick={handleLogin} className="btn btn-microsoft">
              <svg className="ms-logo" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
                <path fill="#f25022" d="M1 1h9.947v9.947H1z"/>
                <path fill="#7fba00" d="M12.053 1H22v9.947h-9.947z"/>
                <path fill="#00adef" d="M1 12.053h9.947v9.947H1z"/>
                <path fill="#ffb900" d="M12.053 12.053H22v9.947h-9.947z"/>
              </svg>
              Sign in with Microsoft
            </button>
          )}

          <div className="footer">
            © 2025 Dealer Task App. All rights reserved.
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;